// The drawer's Touch history section: a timeline of every touch on the opportunity and its
// account, one line per touch, newest first. A line expands in place to show that touch's details
// (GRO-220). Read from `/api/pipeline/{opp_id}/history` when the drawer opens (payload.md, *The
// touch history*).

import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import type { Channel, Direction, HistoryTouch, TouchHistory } from '../api/types.ts';
import { ApiError, fetchHistory } from '../api/client.ts';
import type { TokenGetter } from '../api/client.ts';
import { formatAge, formatDate, formatLongDate } from '../board/format.ts';
import { recordUrl } from '../board/salesforce.ts';
import { CHANNEL_LABELS } from './PipelineTable.tsx';

// The back end lists the newest 100 touches of the last 180 days (payload.md, *The touch history*).
const HISTORY_WINDOW_DAYS = 180;
const HISTORY_TOUCH_LIMIT = 100;

type HistoryState =
  | { status: 'loading' }
  | { status: 'loaded'; history: TouchHistory }
  | { status: 'missing' }
  | { status: 'failed' };

// A 404 means Salesforce no longer has the opportunity (deleted since the board was built).
function historyStateFor(error: unknown): HistoryState {
  if (error instanceof ApiError && error.status === 404) return { status: 'missing' };
  return { status: 'failed' };
}

function useHistory(getToken: TokenGetter, oppId: string): HistoryState {
  const [state, setState] = useState<HistoryState>({ status: 'loading' });
  useEffect(() => {
    let current = true;
    setState({ status: 'loading' });
    fetchHistory(getToken, oppId)
      .then(history => { if (current) setState({ status: 'loaded', history }); })
      .catch((error: unknown) => { if (current) setState(historyStateFor(error)); });
    return () => { current = false; };
  }, [getToken, oppId]);
  return state;
}

// --- The collapsed line ------------------------------------------------------------------------

// One small line icon per channel, drawn in the text colour. The channel's name is beside it for
// screen readers.
const CHANNEL_ICON_PATHS: Record<Channel, ReactNode> = {
  email: <><rect x="1.5" y="3" width="13" height="10" rx="1.5" /><path d="M2 4l6 5 6-5" /></>,
  linkedin: <><rect x="1.5" y="1.5" width="13" height="13" rx="2" /><path d="M5 7v4.5M5 4.6v.1M8 11.5V7m0 2c0-1.2.9-2 2-2s1.5.8 1.5 2v2.5" /></>,
  pylon: <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v6a1.5 1.5 0 0 1-1.5 1.5H7l-3 3v-3h-.5A1.5 1.5 0 0 1 2 9.5z" />,
  call: <path d="M4.5 1.8l1.9 3-1.2 1.4a8 8 0 0 0 4.6 4.6l1.4-1.2 3 1.9-.8 2.3c-6 .4-11.5-5.1-11.1-11.1z" />,
  meeting: <><rect x="1.5" y="2.5" width="13" height="12" rx="1.5" /><path d="M1.5 6.5h13M5 1v3M11 1v3" /></>,
};

function ChannelIcon({ channel }: { channel: Channel }) {
  return (
    <span className="timeline-channel" title={CHANNEL_LABELS[channel]}>
      <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {CHANNEL_ICON_PATHS[channel]}
      </svg>
      <span className="visually-hidden">{CHANNEL_LABELS[channel]}</span>
    </span>
  );
}

// An arrow for which way it went, with the word for screen readers and on hover.
const DIRECTION_ARROWS: Record<Direction, string> = { sent: '→', received: '←', both: '↔' };
const DIRECTION_LABELS: Record<Direction, string> = { sent: 'Sent', received: 'Received', both: 'Both sides' };

function DirectionMark({ direction }: { direction: Direction }) {
  return (
    <span className="timeline-direction" title={DIRECTION_LABELS[direction]}>
      <span aria-hidden="true">{DIRECTION_ARROWS[direction]}</span>
      <span className="visually-hidden">{DIRECTION_LABELS[direction]}</span>
    </span>
  );
}

// Whose side acted: a building for ours, a person for theirs, with the word for screen readers.
const SIDE_ICON_PATHS = {
  internal: <><rect x="3" y="1.5" width="10" height="13" rx="1" /><path d="M6 4.5h1M9 4.5h1M6 7.5h1M9 7.5h1M7 14.5v-3h2v3" /></>,
  external: <><circle cx="8" cy="5" r="2.8" /><path d="M2.5 14.5c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" /></>,
};
const SIDE_LABELS = { internal: 'Internal', external: 'External' };

// Who acted: the customer's contact on a received touch, else our person.
function WhoActed({ touch }: { touch: HistoryTouch }) {
  const side = touch.direction === 'received' ? 'external' : 'internal';
  const name = side === 'external' ? touch.contact?.name : (touch.by?.name ?? 'Unnamed');
  return (
    <span className="timeline-who" title={name ? `${SIDE_LABELS[side]}: ${name}` : SIDE_LABELS[side]}>
      <svg className="timeline-side" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor"
        strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {SIDE_ICON_PATHS[side]}
      </svg>
      {name
        ? <><span className="visually-hidden">{SIDE_LABELS[side]}:</span><span className="timeline-name">{name}</span></>
        : <span className="timeline-name">{SIDE_LABELS[side]}</span>}
    </span>
  );
}

// An automated sequence's email: listed, but it never counts as the last touch (GRO-222).
function isSequence(touch: HistoryTouch): boolean {
  return touch.channel === 'email' && touch.details.sequence;
}

function SequenceLabel() {
  return <span className="timeline-sequence" title="Sent by an automated sequence; not counted as a touch">Sequence</span>;
}

// --- The expanded details ----------------------------------------------------------------------

// A new tab for Gong, Pylon or Salesforce, which open outside the board.
function OutLink({ href, children }: { href: string; children: ReactNode }) {
  return <a className="timeline-link" href={href} target="_blank" rel="noreferrer">{children} ↗</a>;
}

// "Sent by Avery Quill to Robin Castell", reading the right way round for each direction.
function whoToWhom(touch: HistoryTouch): string {
  const ours = touch.by?.name ?? 'someone the source doesn\'t name';
  const contact = touch.contact?.name;
  if (touch.direction === 'sent') return `Sent by ${ours}${contact ? ` to ${contact}` : ''}`;
  if (touch.direction === 'received') return `Received${contact ? ` from ${contact}` : ''} by ${ours}`;
  return `${ours}${contact ? ` with ${contact}` : ''}`;
}

function TextList({ label, lines }: { label: string; lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <>
      <p className="timeline-label">{label}</p>
      <ul className="gong-list">{lines.map((line, index) => <li key={index}>{line}</li>)}</ul>
    </>
  );
}

// What only this channel carries. Gong's and the email's text arrive as plain text, never markup.
function ChannelDetails({ touch }: { touch: HistoryTouch }) {
  switch (touch.channel) {
    case 'email':
      return touch.details.preview
        ? <p className="timeline-preview">{touch.details.preview}</p>
        : <p className="muted">No message text was synced.</p>;
    case 'call': {
      const { brief, key_points: keyPoints, next_steps: nextSteps } = touch.details;
      if (!brief && keyPoints.length === 0 && nextSteps.length === 0) {
        const note = touch.record.object === 'Task' ? 'Logged in Salesforce, with no Gong recording.' : 'Gong wrote no brief for this call.';
        return <p className="muted">{note}</p>;
      }
      return (
        <>
          {brief ? brief.split('\n').map((paragraph, index) => <p key={index}>{paragraph}</p>) : null}
          <TextList label="Key points" lines={keyPoints} />
          <TextList label="Next steps" lines={nextSteps} />
        </>
      );
    }
    case 'meeting': {
      const theirs = touch.details.attendees.filter(attendee => attendee.side === 'theirs').map(attendee => attendee.name);
      const ours = touch.details.attendees.filter(attendee => attendee.side === 'ours').map(attendee => attendee.name);
      if (theirs.length === 0 && ours.length === 0) return <p className="muted">No attendees recorded.</p>;
      return (
        <dl className="timeline-facts">
          {theirs.length > 0 ? <><dt>Their side</dt><dd>{theirs.join(', ')}</dd></> : null}
          {ours.length > 0 ? <><dt>Our side</dt><dd>{ours.join(', ')}</dd></> : null}
        </dl>
      );
    }
    case 'linkedin': {
      const step = [touch.details.step, touch.details.campaign].filter(Boolean).join(' · ');
      return step ? <dl className="timeline-facts"><dt>Step</dt><dd>{step}</dd></dl> : null;
    }
    case 'pylon':
      return null;
  }
}

// Gong's or Pylon's own link, then the record in Salesforce.
function detailLinks(touch: HistoryTouch, instanceUrl: string | null): ReactNode[] {
  const links: ReactNode[] = [];
  if (touch.channel === 'call' && touch.details.url) links.push(<OutLink key="gong" href={touch.details.url}>Gong</OutLink>);
  if (touch.channel === 'pylon' && touch.details.url) links.push(<OutLink key="pylon" href={touch.details.url}>Pylon</OutLink>);
  const salesforceUrl = recordUrl(instanceUrl, touch.record.object, touch.record.id);
  if (salesforceUrl) links.push(<OutLink key="salesforce" href={salesforceUrl}>Salesforce</OutLink>);
  return links;
}

function TouchDetails({ touch, today, instanceUrl, id }: {
  touch: HistoryTouch;
  today: string;
  instanceUrl: string | null;
  id: string;
}) {
  const links = detailLinks(touch, instanceUrl);
  return (
    <div className="timeline-details" id={id}>
      {touch.subject ? <p className="timeline-subject-full">{touch.subject}</p> : null}
      <p className="timeline-meta">
        {formatLongDate(touch.date)} ({formatAge(touch.date, today)}) · {CHANNEL_LABELS[touch.channel]} · {whoToWhom(touch)}
      </p>
      <ChannelDetails touch={touch} />
      {links.length > 0 ? <p className="timeline-links">{links}</p> : null}
    </div>
  );
}

// --- The timeline ------------------------------------------------------------------------------

// One touch: a button that toggles its details. Links sit in the details, outside the button, so
// following one never toggles the line.
function TimelineItem({ touch, today, instanceUrl }: { touch: HistoryTouch; today: string; instanceUrl: string | null }) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  return (
    <li className={'timeline-item' + (open ? ' is-open' : '')}>
      <button type="button" className="timeline-line" aria-expanded={open} aria-controls={detailsId}
        onClick={() => setOpen(!open)}>
        <span className="timeline-date num" title={formatLongDate(touch.date)}>{formatDate(touch.date, today)}</span>
        <ChannelIcon channel={touch.channel} />
        <DirectionMark direction={touch.direction} />
        <span className="timeline-title">
          <span className="timeline-subject">{touch.subject ?? 'No subject'}</span>
          {isSequence(touch) ? <SequenceLabel /> : null}
        </span>
        <WhoActed touch={touch} />
        <span className="timeline-caret" aria-hidden="true">›</span>
      </button>
      {open ? <TouchDetails touch={touch} today={today} instanceUrl={instanceUrl} id={detailsId} /> : null}
    </li>
  );
}

function Timeline({ touches, today, instanceUrl }: { touches: HistoryTouch[]; today: string; instanceUrl: string | null }) {
  if (touches.length === 0) {
    return <p className="muted">No touches in the last {HISTORY_WINDOW_DAYS} days.</p>;
  }
  const limited = touches.length >= HISTORY_TOUCH_LIMIT;

  // Keys are positions: the list never reorders while the drawer is open.
  return (
    <>
      <ol className="timeline">
        {touches.map((touch, index) => <TimelineItem key={index} touch={touch} today={today} instanceUrl={instanceUrl} />)}
      </ol>
      <p className="muted timeline-foot">
        {limited
          ? `The newest ${HISTORY_TOUCH_LIMIT} touches of the last ${HISTORY_WINDOW_DAYS} days.`
          : `Every touch of the last ${HISTORY_WINDOW_DAYS} days, both ways.`}
      </p>
    </>
  );
}

export function TouchHistorySection({
  getToken, oppId, today, instanceUrl,
}: {
  getToken: TokenGetter;
  oppId: string;
  today: string;
  instanceUrl: string | null;
}) {
  const history = useHistory(getToken, oppId);
  return (
    <section className="touch-history">
      <h3>Touch history</h3>
      {history.status === 'loading' ? <p className="muted">Loading…</p> : null}
      {history.status === 'loaded' ? (
        <Timeline touches={history.history.touches} today={today} instanceUrl={instanceUrl} />
      ) : null}
      {history.status === 'missing' ? (
        <p className="muted">Salesforce no longer has this opportunity.</p>
      ) : null}
      {history.status === 'failed' ? (
        <p className="notice notice-error">The touch history couldn't be loaded.</p>
      ) : null}
    </section>
  );
}
